import { getConditionProps } from '@utils/dental-data.utils'
import {
  formatSurfaces,
  formatSurfacesFullNames,
  formatMaterial,
  formatType,
  formatSeverity,
  formatDirection,
  formatFractureType,
  formatCrackType,
  formatRecessionClass,
  formatFurcationLevel,
} from '@utils/tooth-tooltip.utils'
import { isSealantsEligible } from '@utils/tooth.util'
import type { ToothComponentProps } from '../tooth-svgs/types/tooth.types'

export interface ToothTooltipRow {
  label: string
  details: string
}

export type ToothSvgRenderProps = ToothComponentProps

type ConditionProps = ReturnType<typeof getConditionProps>

interface BuildToothViewModelInput {
  toothId: number
  conditionProps: ConditionProps
  hasBleeding: boolean
  showMissingTeeth?: boolean
  showPrimaryTeeth?: boolean
}

export interface ToothViewModel {
  toothComponentProps: ToothSvgRenderProps
  tooltipRows: ToothTooltipRow[]
}

export const buildToothViewModel = ({
  toothId,
  conditionProps,
  hasBleeding,
  showMissingTeeth = true,
  showPrimaryTeeth = true,
}: BuildToothViewModelInput): ToothViewModel => {
  const {
    caries,
    recurring_caries,
    inlay,
    onlay,
    filling,
    drifted,
    crown,
    erosion,
    apicoectomy,
    periapical,
    periradicular,
    root_canal,
    pulpotomy,
    pulpectomy,
    impacted,
    supernumerary,
    sinusTract,
    recession,
    attrition,
    abrasion,
    veneer,
    retainer,
    bridge,
    sealants,
    missing,
    implant,
    furcation,
    crack,
    fracture,
  } = conditionProps

  const displayMissingOverlay = showMissingTeeth && missing.present
  const displayRetainedPrimaryOverlay = showPrimaryTeeth && retainer.present

  const toothComponentProps: ToothSvgRenderProps = {
    conditions: {
      missing: missing.present,
      implant: implant.present,
      rootCanal: root_canal.present,
      pulpotomy: pulpotomy.present,
      pulpectomy: pulpectomy.present,
      bleeding: hasBleeding,
      retainer: retainer.present,
      supernumerary: supernumerary.present,
      sinusTract: sinusTract.present,
      apicoetomy: apicoectomy.present,
      periapical: periapical.present,
      veneer: veneer.present,
      veneerMaterial: veneer.material,
      sealants: isSealantsEligible(toothId) ? sealants.present : false,
    },
    erosion:
      erosion.general ||
      erosion.buccal ||
      erosion.palatal ||
      erosion.lingual ||
      erosion.occlusal
        ? {
            general: erosion.general,
            buccal: erosion.buccal,
            palatal: erosion.palatal,
            lingual: erosion.lingual,
            occlusal: erosion.occlusal,
          }
        : undefined,
    furcation:
      furcation.level || furcation.areas
        ? {
            level: furcation.level ?? undefined,
            areas: furcation.areas,
          }
        : undefined,
    recession: recession.type
      ? {
          type: recession.type,
        }
      : undefined,
    crown: crown.material
      ? {
          material: crown.material,
        }
      : undefined,
    caries:
      caries.surfaces.length > 0 || caries.type || caries.recurrent
        ? {
            surfaces: caries.surfaces,
            type: caries.type,
            recurrent: caries.recurrent || false,
          }
        : undefined,
    recurringCaries:
      recurring_caries.surfaces.length > 0 || recurring_caries.type
        ? {
            surfaces: recurring_caries.surfaces,
            type: recurring_caries.type,
          }
        : undefined,
    inlay:
      inlay.surfaces.length > 0 || inlay.material
        ? {
            surfaces: inlay.surfaces,
            material: inlay.material,
          }
        : undefined,
    onlay:
      onlay.surfaces.length > 0 || onlay.material
        ? {
            surfaces: onlay.surfaces,
            material: onlay.material,
          }
        : undefined,
    filling:
      filling.surfaces.length > 0 || filling.material
        ? {
            surfaces: filling.surfaces,
            material: filling.material,
          }
        : undefined,
    impacted: impacted.direction
      ? {
          direction: impacted.direction,
        }
      : undefined,
    drifted:
      drifted.surfaces.length > 0
        ? {
            surfaces: drifted.surfaces,
          }
        : undefined,
    bridge:
      bridge.connector || bridge.data.length > 0 || bridge.material
        ? {
            connector:
              bridge.connector ?? (bridge.data.length > 0 ? true : undefined),
            source: bridge.data.some((data) => data.condition === 'abutment')
              ? 'abutment'
              : bridge.data.some((data) => data.condition === 'bridge')
                ? 'bridge'
                : undefined,
            material: bridge.material ?? undefined,
          }
        : undefined,
    attrition: attrition.severity
      ? {
          severity: attrition.severity,
        }
      : undefined,
    abrasion:
      abrasion.severity ||
      abrasion.buccal ||
      abrasion.palatal ||
      abrasion.lingual
        ? {
            severity: abrasion.severity,
            buccal: abrasion.buccal,
            palatal: abrasion.palatal,
            lingual: abrasion.lingual,
          }
        : undefined,
    periradicular: periradicular.severity
      ? {
          present: true,
          severity: periradicular.severity,
        }
      : undefined,
    crack:
      crack.types.length > 0 || crack.recurrent
        ? {
            types: crack.types,
            recurrent: crack.recurrent,
          }
        : undefined,
    fracture:
      fracture.types.length > 0 || fracture.recurrent
        ? {
            types: fracture.types,
            recurrent: fracture.recurrent,
          }
        : undefined,
    showMissingOverlay: displayMissingOverlay,
    showRetainedPrimaryOverlay: displayRetainedPrimaryOverlay,
  }

  const tooltipRows: ToothTooltipRow[] = []

  if (recurring_caries.surfaces.length > 0 || recurring_caries.type) {
    const details: string[] = []
    if (recurring_caries.type) details.push(formatType(recurring_caries.type))
    if (recurring_caries.surfaces.length > 0)
      details.push(formatSurfaces(recurring_caries.surfaces))
    tooltipRows.push({
      label: 'Recurring caries',
      details: details.join(' • '),
    })
  }

  if (caries.surfaces.length > 0 || caries.type || caries.recurrent) {
    const details: string[] = []
    if (caries.type) details.push(formatType(caries.type))
    if (caries.recurrent) details.push('Recurrent')
    if (caries.surfaces.length > 0)
      details.push(formatSurfaces(caries.surfaces))
    tooltipRows.push({ label: 'Caries', details: details.join(' • ') })
  }

  if (filling.surfaces.length > 0 || filling.material) {
    const details: string[] = []
    if (filling.material) details.push(formatMaterial(filling.material))
    if (filling.surfaces.length > 0)
      details.push(formatSurfaces(filling.surfaces))
    tooltipRows.push({ label: 'Filling', details: details.join(' • ') })
  }

  if (inlay.surfaces.length > 0 || inlay.material) {
    const details: string[] = []
    if (inlay.material) details.push(formatMaterial(inlay.material))
    if (inlay.surfaces.length > 0) details.push(formatSurfaces(inlay.surfaces))
    tooltipRows.push({ label: 'Inlay', details: details.join(' • ') })
  }

  if (onlay.surfaces.length > 0 || onlay.material) {
    const details: string[] = []
    if (onlay.material) details.push(formatMaterial(onlay.material))
    if (onlay.surfaces.length > 0) details.push(formatSurfaces(onlay.surfaces))
    tooltipRows.push({ label: 'Onlay', details: details.join(' • ') })
  }

  if (crown.material) {
    tooltipRows.push({
      label: 'Crown',
      details: formatMaterial(crown.material),
    })
  }

  if (veneer.present || veneer.material) {
    tooltipRows.push({
      label: 'Veneer',
      details: veneer.material ? formatMaterial(veneer.material) : 'Present',
    })
  }

  if (
    erosion.general ||
    erosion.buccal ||
    erosion.palatal ||
    erosion.lingual ||
    erosion.occlusal
  ) {
    const surfaces: string[] = []
    if (erosion.general) surfaces.push('General')
    if (erosion.buccal) surfaces.push('Buccal')
    if (erosion.palatal) surfaces.push('Palatal')
    if (erosion.lingual) surfaces.push('Lingual')
    if (erosion.occlusal) surfaces.push('Occlusal')
    tooltipRows.push({ label: 'Erosion', details: surfaces.join(', ') })
  }

  if (crack.types.length > 0 || crack.recurrent) {
    const details: string[] = []
    if (crack.types.length > 0) {
      details.push(crack.types.map((type) => formatCrackType(type)).join(', '))
    }
    if (crack.recurrent) details.push('Recurrent')
    tooltipRows.push({ label: 'Crack', details: details.join(' • ') })
  }

  if (fracture.types.length > 0 || fracture.recurrent) {
    const details: string[] = []
    if (fracture.types.length > 0) {
      details.push(
        fracture.types.map((type) => formatFractureType(type)).join(', '),
      )
    }
    if (fracture.recurrent) details.push('Recurrent')
    tooltipRows.push({ label: 'Fracture', details: details.join(' • ') })
  }

  if (recession.type) {
    tooltipRows.push({
      label: 'Recession',
      details: formatRecessionClass(recession.type),
    })
  }

  if (furcation.level) {
    tooltipRows.push({
      label: 'Furcation',
      details: formatFurcationLevel(furcation.level),
    })
  }

  if (attrition.severity) {
    tooltipRows.push({
      label: 'Attrition',
      details: formatSeverity(attrition.severity),
    })
  }

  if (
    abrasion.severity ||
    abrasion.buccal ||
    abrasion.palatal ||
    abrasion.lingual
  ) {
    const details: string[] = []
    if (abrasion.severity) {
      details.push(formatSeverity(abrasion.severity))
    }
    const surfaces: string[] = []
    if (abrasion.buccal) surfaces.push('Buccal')
    if (abrasion.palatal) surfaces.push('Palatal')
    if (abrasion.lingual) surfaces.push('Lingual')
    if (surfaces.length > 0) {
      details.push(surfaces.join(', '))
    }
    tooltipRows.push({
      label: 'Abrasion/abfraction',
      details: details.join(' • '),
    })
  }

  if (impacted.direction) {
    tooltipRows.push({
      label: 'Impacted',
      details: formatDirection(impacted.direction),
    })
  }

  if (bridge.material) {
    const details: string[] = []
    if (bridge.material) details.push(formatMaterial(bridge.material))
    tooltipRows.push({ label: 'Bridge', details: details.join(' • ') })
  }

  if (drifted.surfaces.length > 0) {
    tooltipRows.push({
      label: 'Drifted',
      details: formatSurfacesFullNames(drifted.surfaces),
    })
  }

  if (missing.present) tooltipRows.push({ label: 'Missing', details: '' })
  if (implant.present) tooltipRows.push({ label: 'Implant', details: '' })
  if (root_canal.present) tooltipRows.push({ label: 'Root canal', details: '' })
  if (pulpotomy.present) tooltipRows.push({ label: 'Pulpotomy', details: '' })
  if (pulpectomy.present) tooltipRows.push({ label: 'Pulpectomy', details: '' })
  if (apicoectomy.present)
    tooltipRows.push({ label: 'Apicoectomy', details: '' })
  if (periapical.present) tooltipRows.push({ label: 'Periapical', details: '' })
  if (periradicular.severity) {
    tooltipRows.push({
      label: 'Periradicular',
      details: formatSeverity(periradicular.severity),
    })
  }
  if (supernumerary.present) {
    tooltipRows.push({ label: 'Supernumerary', details: '' })
  }
  if (sinusTract.present)
    tooltipRows.push({ label: 'Sinus tract', details: '' })
  if (retainer.present)
    tooltipRows.push({ label: 'Retained primary', details: '' })
  if (sealants.present) tooltipRows.push({ label: 'Sealants', details: '' })
  if (hasBleeding) tooltipRows.push({ label: 'Bleeding', details: '' })

  return {
    toothComponentProps,
    tooltipRows,
  }
}
